import {
  FETCH_WEEK_SCHEDULES,
  INITIALIZE,
  SORT_DAY_SCHEDULES,
  WEEK_SCHEDULES_FETCHED,
  clear,
  closeWeekMenu,
  daySchedulesSorted,
  fetchWeekSchedules,
  sortDaySchedules,
  weekSchedulesFetchError,
  weekSchedulesFetched,
} from '../store/modules/schedule'
import {
  __,
  add,
  always,
  equals,
  evolve,
  ifElse,
  isEmpty,
  isNil,
  o,
  pipe,
  prop,
  test as regexTest,
  filter as rfilter,
  map as rmap,
  sort,
  subtract,
  when,
} from 'ramda'
import { combineEpics, ofType } from 'redux-observable'
import {
  filter,
  map,
  mergeMap,
  withLatestFrom,
} from 'rxjs'
import { logObservableError, logObservableErrorAndTriggerAction } from '../utilities/logs'
import { getSchedulesDayName, toComputerDate, toComputerTime } from '../utilities/datetimes'
import { CHANGE_ROUTE } from '../store/modules/router'
import { ROUTE_FOUND } from '../store/modules/router'
import { TOGGLE_COLLAPSED } from '../store/modules/header'

// incrementWeekDay :: Number -> Number
const incrementWeekDay = ifElse(
  equals(6),
  always(0),
  add(1),
)

// Converts i24NEWS schedule days schema
//
// sun | mon | tue | wed | thu | fri | sat
//  6  |  0  |  1  |  2  |  3  |  4  |  5
//
// to universal time format (UTC)
//
// sun | mon | tue | wed | thu | fri | sat
//  0  |  1  |  2  |  3  |  4  |  5  |  6
//
// converti24DayToUTC :: Number -> Number
export const converti24DayToUTC = incrementWeekDay

// weekdayToDateString :: Date -> Number -> String
export const weekdayToDateString = now => pipe(
  subtract(__, now.getDay()),
  add(now.getDate()),
  date => now.setDate(date),
  () => toComputerDate(now, '-'),
)

// calculateUTCDateString :: (Date, Number, String) -> String
export const calculateUTCDateString = (now, weekday, time) =>
  `${weekdayToDateString(now)(weekday)}T${time}:00Z`

// calculateStartDate :: (Date, Schedule) -> String
const calculateStartDate = (now, schedule) => calculateUTCDateString(now, schedule.day, schedule.startHour)

// calculateEndDate :: (Date, Schedule) -> String
const calculateEndDate = (now, schedule) => calculateUTCDateString(
  now,
  calculateToWeekday(schedule),
  schedule.endHour,
)

// the day returned by the api is ALWAYS the day at which the show begins. Thus,
// it can potentially ends the day after (example if it begins at 23:00 and
// ends at 01:00)
//
// in that specific case, we need to return the next day
//
// calculateToWeekday :: Schedule -> Number
export const calculateToWeekday = ifElse(
  schedule => schedule.endHour <= schedule.startHour,
  o(incrementWeekDay, prop('day')),
  prop('day'),
)

// formatSchedule :: Document -> Schedule -> Schedule
export const formatSchedule = (document, now) => pipe(
  evolve({ day: converti24DayToUTC }),
  when(
    schedule => !isNil(schedule.show),
    schedule => ({
      ...schedule,
      from: calculateStartDate(now, schedule),
      to: calculateEndDate(now, schedule),
    }),
  ),
)

// initializeSchedulesEpic :: Epic -> Observable Action
export const initializeSchedulesEpic = (action$, state$, { logger }) =>
  action$.pipe(
    ofType(INITIALIZE),
    withLatestFrom(state$),
    map(([ action, state ]) => isEmpty(state.schedule.weekSchedules)
      ? fetchWeekSchedules()
      : sortDaySchedules(),
    ),
    logObservableError('schedules :: initializeSchedulesEpic', logger),
  )

// fetchWeekSchedulesEpic :: Epic -> Observable Action.WEEK_SCHEDULES_FETCHED
export const fetchWeekSchedulesEpic = (action$, state$, { document, fetchApi, logger }) =>
  action$.pipe(
    ofType(FETCH_WEEK_SCHEDULES),
    withLatestFrom(state$),
    mergeMap(([ _, state ]) => fetchApi(`/v2/${state.router.locale}/schedules`)),
    map(response => weekSchedulesFetched(
      rmap(formatSchedule(document, new Date()), response.body || []),
    )),
    logObservableErrorAndTriggerAction(
      'schedules :: fetchWeekSchedulesEpic',
      weekSchedulesFetchError,
      logger,
    ),
  )

// Sort the schedules in case they were not on the same UTC day
// and on a week boundary (monday / sunday)
//
// When consulting the monday schedules on GMT+2, we should see some
// schedules from sunday evening on UTC.
// These sunday evening schedules are at the end of the UTC weekSchedules
// list. So just picking elements of this list doesn't work when having
// a time window overlapsing between the start / end of the week.
//
// If we don't sort, we'll have the following daySchedules list :
//
// [
//   UTC monday morning
//   UTC monday afternoon
//   UTC monday evening
//   UTC sunday evening
// ]
//
// whereas we want to have
//
// [
//   UTC sunday evening
//   UTC monday morning
//   UTC monday afternoon
//   UTC monday evening
// ]
//
// That's why the sort has to be done, even if in most of the cases
// it'll re-sort already ordered items.
//
// sortSchedulesByTime :: [Schedule] -> [Schedule]
export const sortSchedulesByTime =  sort(pipe(
  (a, b) => ({
    aTime: toComputerTime(a.from),
    bTime: toComputerTime(b.from),
  }),
  ifElse(
    ({ aTime, bTime }) => aTime === bTime,
    always(0),
    ({ aTime, bTime }) => aTime < bTime ? -1 : 1,
  ),
))

// sortDaySchedulesEpic :: Epic -> Observable Action.DAY_SCHEDULES_SORTED
export const sortDaySchedulesEpic = (action$, state$, { logger }) =>
  action$.pipe(
    ofType(WEEK_SCHEDULES_FETCHED, SORT_DAY_SCHEDULES),
    withLatestFrom(state$),
    map(([ action, state ]) => ({
      weekSchedules: state.schedule.weekSchedules,
      day: state.router.activeRoute.params.day,
      locale: state.router.locale,
    })),
    map(({ weekSchedules, locale, day }) => pipe(
      rfilter(schedule => day === getSchedulesDayName(locale)(new Date(schedule.from))),
      sortSchedulesByTime,
    )(weekSchedules)),
    map(daySchedulesSorted),
    logObservableError('schedules :: sortDaySchedulesEpic', logger),
  )

// clearSchedulesEpic :: Epic -> Observable Action.CLEAR
export const clearSchedulesEpic = (action$, state$, { logger }) =>
  action$.pipe(
    ofType(ROUTE_FOUND),
    withLatestFrom(state$),
    // only clear the weekSchedule when leaving the schedule page
    // (ie keep them when navigating from a schedule day to another)
    filter(([ action, state ]) =>
      !isEmpty(state.schedule.weekSchedules)
      && !regexTest(/^schedules/, action.name),
    ),
    map(clear),
    logObservableError('schedules :: clearSchedulesEpic', logger),
  )

// closeWeekMenuEpic :: Epic -> Observable Action TOGGLE_SUB_CATEGORIES
export const closeWeekMenuEpic = (action$, state$, { logger }) =>
  action$.pipe(
    ofType(
      CHANGE_ROUTE,
      TOGGLE_COLLAPSED,
    ),
    withLatestFrom(state$),
    filter(([_, state]) => state.schedule.weekMenuOpened),
    filter(([ action, state ]) => !(action.type === TOGGLE_COLLAPSED && state.header.collapsed)),
    map(closeWeekMenu),
    logObservableError('schedules :: closeWeekMenuEpic', logger),
  )

export default combineEpics(
  clearSchedulesEpic,
  closeWeekMenuEpic,
  fetchWeekSchedulesEpic,
  initializeSchedulesEpic,
  sortDaySchedulesEpic,
)
